import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Filter, FolderOpen, Plus, X, CalendarClock, Building2, Truck, FileText, Hammer, Type } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import type { FascicoloList, TipoFascicolo, StatoFascicolo, PersonaleMinimal, EntityOption } from '../../../types/fascicoli'

const statoBadge: Record<StatoFascicolo, { label: string; bg: string; text: string }> = {
  aperto: { label: 'Aperto', bg: 'bg-blue-100', text: 'text-blue-700' },
  chiuso: { label: 'Chiuso', bg: 'bg-green-100', text: 'text-green-700' },
  archiviato: { label: 'Archiviato', bg: 'bg-gray-100', text: 'text-gray-700' },
}

const tipoConfig: Record<TipoFascicolo, { label: string; icon: React.ElementType; color: string }> = {
  cliente: { label: 'Cliente', icon: Building2, color: 'text-blue-600' },
  fornitore: { label: 'Fornitore', icon: Truck, color: 'text-orange-600' },
  offerta: { label: 'Offerta', icon: FileText, color: 'text-purple-600' },
  commessa: { label: 'Commessa', icon: Hammer, color: 'text-emerald-600' },
  testo_libero: { label: 'Progetto', icon: Type, color: 'text-gray-600' },
}

// Tipi selezionabili per creazione manuale (offerta/commessa sono automatici)
const tipiCreabili: TipoFascicolo[] = ['cliente', 'fornitore', 'testo_libero']

export default function FascicoliPage() {
  const navigate = useNavigate()
  const [fascicoli, setFascicoli] = useState<FascicoloList[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStato, setFilterStato] = useState<StatoFascicolo | ''>('')
  const [filterTipo, setFilterTipo] = useState<TipoFascicolo | ''>('')
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  // Modale creazione
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({
    titolo: '',
    tipo: '' as TipoFascicolo | '',
    entity_id: '',
    progetto_testo: '',
    responsabile_id: '',
    descrizione: '',
  })
  const [creating, setCreating] = useState(false)

  // Dropdown data
  const [personale, setPersonale] = useState<PersonaleMinimal[]>([])
  const [entityOptions, setEntityOptions] = useState<EntityOption[]>([])
  const [loadingEntities, setLoadingEntities] = useState(false)

  useEffect(() => {
    loadData()
    loadPersonale()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const res = await api.get('/v1/fascicoli')
      setFascicoli(res.data)
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadPersonale = async () => {
    try {
      const res = await api.get('/v1/personale')
      setPersonale(res.data.filter((p: any) => p.stato === 'attivo'))
    } catch { /* ignore */ }
  }

  const loadEntities = async (tipo: TipoFascicolo) => {
    if (tipo === 'testo_libero') {
      setEntityOptions([])
      return
    }
    setLoadingEntities(true)
    try {
      let options: EntityOption[] = []
      if (tipo === 'cliente') {
        const res = await api.get('/v1/anagrafiche/clienti')
        options = res.data.map((c: any) => ({ id: c.id, label: c.ragione_sociale }))
      } else if (tipo === 'fornitore') {
        const res = await api.get('/v1/magazzino/fornitori')
        options = res.data.map((f: any) => ({ id: f.id, label: f.ragione_sociale }))
      } else if (tipo === 'offerta') {
        const res = await api.get('/v1/offerte')
        options = res.data.map((o: any) => ({ id: o.id, label: `${o.numero_offerta} - ${o.titolo}` }))
      } else if (tipo === 'commessa') {
        const res = await api.get('/v1/commesse')
        options = res.data.map((c: any) => ({ id: c.id, label: `${c.numero_commessa} - ${c.titolo}` }))
      }
      setEntityOptions(options)
    } catch {
      setEntityOptions([])
    } finally {
      setLoadingEntities(false)
    }
  }

  const handleTipoChange = (tipo: TipoFascicolo) => {
    setCreateForm({ ...createForm, tipo, entity_id: '', progetto_testo: '' })
    loadEntities(tipo)
  }

  const handleCreate = async () => {
    const isEntityType = createForm.tipo === 'cliente' || createForm.tipo === 'fornitore'
    if (!createForm.tipo || !createForm.responsabile_id) {
      setToast({ message: 'Compila tipo e responsabile', type: 'error' })
      return
    }
    if (isEntityType && !createForm.entity_id) {
      setToast({ message: 'Seleziona l\'entita\' collegata', type: 'error' })
      return
    }
    if (createForm.tipo === 'testo_libero' && !createForm.progetto_testo) {
      setToast({ message: 'Inserisci il nome del progetto', type: 'error' })
      return
    }
    if (createForm.tipo === 'testo_libero' && !createForm.titolo) {
      setToast({ message: 'Inserisci il titolo del fascicolo', type: 'error' })
      return
    }

    setCreating(true)
    try {
      await api.post('/v1/fascicoli', {
        titolo: isEntityType ? undefined : createForm.titolo,
        tipo: createForm.tipo,
        entity_id: createForm.tipo !== 'testo_libero' ? createForm.entity_id : undefined,
        progetto_testo: createForm.tipo === 'testo_libero' ? createForm.progetto_testo : undefined,
        responsabile_id: createForm.responsabile_id,
        descrizione: createForm.descrizione || undefined,
      })
      setToast({ message: 'Fascicolo creato!', type: 'success' })
      setShowCreate(false)
      setCreateForm({ titolo: '', tipo: '', entity_id: '', progetto_testo: '', responsabile_id: '', descrizione: '' })
      loadData()
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      if (err?.response?.status === 409) {
        setToast({ message: detail || 'Fascicolo gia\' esistente per questa entita\'', type: 'error' })
      } else {
        setToast({ message: detail || 'Errore nella creazione', type: 'error' })
      }
    } finally {
      setCreating(false)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('it-IT')
  }

  const filtered = fascicoli.filter(f => {
    const matchSearch = !search ||
      f.titolo.toLowerCase().includes(search.toLowerCase()) ||
      f.numero_fascicolo.toLowerCase().includes(search.toLowerCase()) ||
      (f.entity_label || '').toLowerCase().includes(search.toLowerCase()) ||
      (f.progetto_testo || '').toLowerCase().includes(search.toLowerCase())
    const matchStato = !filterStato || f.stato === filterStato
    const matchTipo = !filterTipo || f.tipo === filterTipo
    return matchSearch && matchStato && matchTipo
  })

  return (
    <div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fascicoli</h1>
          <p className="text-gray-500">Dossier e tracciamento attivita'</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nuovo Fascicolo
        </button>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca fascicoli..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value as TipoFascicolo | '')}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white"
            >
              <option value="">Tutti i tipi</option>
              <option value="cliente">Cliente</option>
              <option value="fornitore">Fornitore</option>
              <option value="offerta">Offerta</option>
              <option value="commessa">Commessa</option>
              <option value="testo_libero">Progetto</option>
            </select>
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filterStato}
              onChange={(e) => setFilterStato(e.target.value as StatoFascicolo | '')}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white"
            >
              <option value="">Tutti gli stati</option>
              <option value="aperto">Aperto</option>
              <option value="chiuso">Chiuso</option>
              <option value="archiviato">Archiviato</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FolderOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            {search || filterStato || filterTipo
              ? 'Nessun fascicolo trovato con i filtri selezionati'
              : 'Nessun fascicolo presente. Crea il primo fascicolo per iniziare.'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numero</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titolo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo / Collegamento</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responsabile</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Azioni</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prossima Scadenza</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((f) => {
                const badge = statoBadge[f.stato]
                const tipo = tipoConfig[f.tipo]
                const TipoIcon = tipo.icon
                const scadenzaDate = f.prossima_scadenza ? new Date(f.prossima_scadenza) : null
                const isScaduta = scadenzaDate && scadenzaDate < new Date()
                const isUrgente = scadenzaDate && !isScaduta && scadenzaDate.getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000

                return (
                  <tr
                    key={f.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/app/fascicoli/${f.id}`)}
                  >
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                        {f.numero_fascicolo}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{f.titolo}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <TipoIcon className={`w-4 h-4 ${tipo.color}`} />
                        <div>
                          <div className="text-xs text-gray-500">{tipo.label}</div>
                          <div className="text-sm text-gray-700">
                            {f.tipo === 'testo_libero' ? f.progetto_testo : f.entity_label}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{f.responsabile_nome || '-'}</td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-sm font-medium">{f.azioni_count}</span>
                        {f.azioni_aperte > 0 && (
                          <span className="text-xs text-blue-600">({f.azioni_aperte} aperte)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {f.prossima_scadenza ? (
                        <div className="flex items-center gap-1.5">
                          <CalendarClock className={`w-4 h-4 ${isScaduta ? 'text-red-500' : isUrgente ? 'text-amber-500' : 'text-gray-400'}`} />
                          <span className={`text-sm ${isScaduta ? 'text-red-600 font-medium' : isUrgente ? 'text-amber-600' : 'text-gray-600'}`}>
                            {formatDate(f.prossima_scadenza)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modale Creazione */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Nuovo Fascicolo</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Tipo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                <div className="grid grid-cols-3 gap-2">
                  {tipiCreabili.map(key => {
                    const cfg = tipoConfig[key]
                    const Icon = cfg.icon
                    return (
                      <button
                        key={key}
                        onClick={() => handleTipoChange(key)}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-colors text-xs ${
                          createForm.tipo === key
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${cfg.color}`} />
                        {cfg.label}
                      </button>
                    )
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-1">I fascicoli offerta/commessa vengono creati automaticamente</p>
              </div>

              {/* Entity selector o testo libero */}
              {createForm.tipo && createForm.tipo !== 'testo_libero' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tipoConfig[createForm.tipo].label} *
                  </label>
                  <select
                    value={createForm.entity_id}
                    onChange={(e) => setCreateForm({ ...createForm, entity_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={loadingEntities}
                  >
                    <option value="">{loadingEntities ? 'Caricamento...' : 'Seleziona...'}</option>
                    {entityOptions.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {createForm.tipo === 'testo_libero' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome Progetto *</label>
                  <input
                    type="text"
                    value={createForm.progetto_testo}
                    onChange={(e) => setCreateForm({ ...createForm, progetto_testo: e.target.value })}
                    placeholder="Es: Certificazione ISO 9001, Progetto Internazionalizzazione..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}

              {/* Titolo - solo per testo_libero (per cliente/fornitore e' automatico) */}
              {createForm.tipo === 'testo_libero' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titolo Fascicolo *</label>
                  <input
                    type="text"
                    value={createForm.titolo}
                    onChange={(e) => setCreateForm({ ...createForm, titolo: e.target.value })}
                    placeholder="Titolo descrittivo del fascicolo"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}
              {(createForm.tipo === 'cliente' || createForm.tipo === 'fornitore') && createForm.entity_id && (
                <p className="text-sm text-gray-500 italic">
                  Il titolo sara' generato automaticamente: &quot;Fascicolo {entityOptions.find(e => e.id === createForm.entity_id)?.label || '...'}&quot;
                </p>
              )}

              {/* Responsabile */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Responsabile *</label>
                <select
                  value={createForm.responsabile_id}
                  onChange={(e) => setCreateForm({ ...createForm, responsabile_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Seleziona responsabile...</option>
                  {personale.map(p => (
                    <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>
                  ))}
                </select>
              </div>

              {/* Descrizione */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <textarea
                  value={createForm.descrizione}
                  onChange={(e) => setCreateForm({ ...createForm, descrizione: e.target.value })}
                  rows={2}
                  placeholder="Descrizione opzionale..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {creating ? 'Creazione...' : 'Crea Fascicolo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
